import type { PropsWithChildren } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { Masonry } from '@elfsight/lib-masonry-grid';
import { useWindowWidth } from '@elfsight/lib-utils';
import { useDebounceCallback } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import { useRect } from 'react-use-rect';
import {
  useDeepCompareCallback,
  useIconSectionColor,
  useItemsInRow
} from '../../../../helpers';
import { Icon } from '../../../common';
import { ItemLayout } from '../items-layouts';
import { useData } from '../main-layouts';
import { SectionsTitle } from './SectionsTitle';

export function SectionsLayoutMasonry() {
  const [itemsCount, containerRef, columnBasis] = useItemsInRow();
  const { visibleSections, itemsBySections } = useData();
  const [updateHeightEvent, setUpdateHeightEvent] = useState(false);
  const windowWidth = useWindowWidth();

  const heightChangeHandler = useDebounceCallback(() => {
    setUpdateHeightEvent((prevState) => !prevState);
  });

  const {
    sectionTitleFontStyle: { fontSize: sectionTitleFontSize }
  } = useSettings();

  const iconSize = sectionTitleFontSize * 1.333;
  const iconColor = useIconSectionColor();
  const columns = itemsCount ?? 1;

  const renderContentItems = useDeepCompareCallback(
    (id: string) => {
      return itemsBySections?.[id].map((menuItem) => (
        <HeightObservableSection
          key={menuItem.id}
          onHeightChange={heightChangeHandler}
        >
          <ItemLayout data={menuItem} />
        </HeightObservableSection>
      ));
    },
    [itemsBySections, windowWidth]
  );

  if (visibleSections?.length === 1) {
    return (
      <Component ref={containerRef}>
        <Masonry
          columns={columns}
          basis={columnBasis}
          updateHeights={updateHeightEvent}
        >
          {renderContentItems(visibleSections[0].id)}
        </Masonry>
      </Component>
    );
  }

  return (
    <Component ref={containerRef}>
      {visibleSections?.length > 1 && (
        <Masonry
          columns={columns}
          basis={columnBasis}
          updateHeights={updateHeightEvent}
        >
          {visibleSections.map(({ name, id, icon }) => (
            <HeightObservableSection
              onHeightChange={heightChangeHandler}
              key={id}
            >
              <SectionContainer>
                <Title>
                  {icon && (
                    <Icon
                      icon={icon}
                      size={iconSize}
                      fill={iconColor}
                      style={{ marginRight: '12px' }}
                      alt=""
                    />
                  )}
                  <TitleText>{name}</TitleText>
                </Title>

                <Items>{renderContentItems(id)}</Items>
              </SectionContainer>
            </HeightObservableSection>
          ))}
        </Masonry>
      )}
    </Component>
  );
}

const HeightObservableSection = ({
  children,
  onHeightChange,
  ...forwardingProps
}: PropsWithChildren & { onHeightChange: Function }) => {
  const [itemHeight, setItemHeight] = useState(0);
  const [ref] = useRect(
    ({ height }) => {
      if (itemHeight !== height) {
        setItemHeight(height);
        onHeightChange?.(height);
      }
    },
    { resize: true }
  );

  return (
    <div
      className="eapp-restaurant-menu-masonry-item"
      ref={ref}
      {...forwardingProps}
    >
      {children}
    </div>
  );
};

const Component = styled.div`
  margin: -16px -24px -6px;
`;

const SectionContainer = styled.div`
  padding: 16px 24px 32px;
`;

const Title = styled(SectionsTitle)`
  justify-content: center;
`;

const TitleText = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Items = styled.div`
  min-width: 100%;
  margin: -16px -24px;
  display: flex;
  flex-direction: column;
`;
